import React, { useState } from 'react';
import { ApiService } from 'services/ApiService';
import type { SecurityDepositEnrollment } from 'models/enrollments/SecurityDepositEnrollment';

export interface FinalizeSecurityDepositFormProps {
  enrollment: SecurityDepositEnrollment;
  /** Called after a successful finalize so the parent can refresh the enrollment. */
  onFinalized: () => void;
  isDisabled?: boolean;
}

/**
 * Team-facing form shown in the Ops Hub when a security deposit node is awaiting
 * team input. Collects the per-client amount and submits it to the finalize endpoint;
 * only after that can the client see and pay the deposit.
 */
export const FinalizeSecurityDepositForm: React.FC<FinalizeSecurityDepositFormProps> = ({
  enrollment,
  onFinalized,
  isDisabled = false,
}) => {
  const [amountUsd, setAmountUsd] = useState('');
  const [description, setDescription] = useState(enrollment.description ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedAmount = parseFloat(amountUsd);
  const isValid = !Number.isNaN(parsedAmount) && parsedAmount > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await ApiService.finalizeSecurityDeposit(enrollment.id, {
        amountMinorUnits: Math.round(parsedAmount * 100),
        currency: enrollment.currency || 'usd',
        description: description.trim() || undefined,
      });
      onFinalized();
    } catch {
      setError('Could not finalize the deposit. Please check the amount and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="p-4 bg-white border border-indigo-200 rounded-lg shadow-sm space-y-4"
      aria-label="Finalize security deposit"
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-800">Set Up Security Deposit</h2>
        <span className="inline-flex items-center rounded-full px-3 py-0.5 text-xs font-semibold bg-indigo-100 text-indigo-700 border border-indigo-300">
          Team action needed
        </span>
      </div>

      <p className="text-sm text-gray-600">
        Enter the deposit amount for this client. Once you confirm, the client will be able to view
        and pay the deposit securely through Stripe.
      </p>

      <div>
        <label className="block text-xs text-gray-600 mb-1" htmlFor={`deposit-amount-${enrollment.id}`}>
          Deposit amount ({(enrollment.currency || 'usd').toUpperCase()})
        </label>
        <input
          id={`deposit-amount-${enrollment.id}`}
          type="number"
          min="0.01"
          step="0.01"
          value={amountUsd}
          onChange={(e) => setAmountUsd(e.target.value)}
          placeholder="e.g. 250.00"
          disabled={isDisabled || submitting}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline disabled:opacity-50"
        />
      </div>

      <div>
        <label className="block text-xs text-gray-600 mb-1" htmlFor={`deposit-description-${enrollment.id}`}>
          Description (optional — shown to the client)
        </label>
        <input
          id={`deposit-description-${enrollment.id}`}
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Booking security deposit"
          disabled={isDisabled || submitting}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline disabled:opacity-50"
        />
      </div>

      {error && (
        <p className="text-sm text-red-600" role="alert">{error}</p>
      )}

      <button
        type="submit"
        disabled={isDisabled || submitting || !isValid}
        className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting && (
          <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        )}
        {submitting ? 'Finalizing…' : 'Confirm & Send to Client'}
      </button>
    </form>
  );
};

export default FinalizeSecurityDepositForm;
